// Slice-internal Konva renderer for smileys.
//
// Smileys render one of 22 multi-colour SVG motifs (see smiley-icons.config.ts)
// as a `Konva.Image`, drawn from an SVG data URL — the same approach as the
// person avatars (NOT the old emoji-rasterisation path). The motif's `id="fill"`
// element is recoloured with the object's `fill`, so the fill swatch in the
// options circle tints the smiley. Image decoding is async; the layer is
// repainted on load.

import Konva from 'konva';
import type { SliceRenderContext } from '../../core/object-registry.js';
import type { SmileyObject } from './smiley.interface.js';
import { SMILEY_ICONS, DEFAULT_SMILEY_ICON } from './smiley-icons.config.js';

export const SMILEY_IMAGE_NAME = 'smiley-image';
export const SMILEY_HIT_NAME = 'smiley-hit';

/**
 * Fit the motif into the object box preserving its aspect ratio (contain),
 * centred. The motifs have differing viewBoxes, so a non-square box centres the
 * icon rather than distorting it.
 */
function iconBox(obj: SmileyObject): { x: number; y: number; width: number; height: number } {
  const def = SMILEY_ICONS[obj.icon as keyof typeof SMILEY_ICONS] ?? SMILEY_ICONS[DEFAULT_SMILEY_ICON];
  const vbW = def.vbWidth > 0 ? def.vbWidth : 1;
  const vbH = def.vbHeight > 0 ? def.vbHeight : 1;
  const scale = Math.min(obj.width / vbW, obj.height / vbH);
  const width = vbW * scale;
  const height = vbH * scale;
  return { x: (obj.width - width) / 2, y: (obj.height - height) / 2, width, height };
}

/**
 * Recolour ONLY the motif's `id="fill"` element. Motifs without an `id="fill"`
 * (e.g. icon2) are returned unchanged, so this is safe for every motif.
 */
function tintFill(svg: string, fill: string | undefined): string {
  if (!fill) return svg;
  return svg.replace(/<[a-z]+\s+id="fill"[^>]*\/>/, (tag) =>
    /\bfill="[^"]*"/.test(tag)
      ? tag.replace(/\bfill="[^"]*"/, `fill="${fill}"`)
      : tag.replace(/\s*\/>$/, ` fill="${fill}"/>`),
  );
}

/** Signature of everything baked into the bitmap: the motif key + fill colour. */
function iconSignature(obj: SmileyObject): string {
  return `${obj.icon}|${obj.fill ?? ''}`;
}

/**
 * Das fertig getoente Motiv-SVG als Zeichenkette.
 *
 * Auch vom SVG-Export genutzt (core/export): echte Vektorpfade statt der
 * Pixel-Fassung, mit derselben Toenung.
 */
export function smileySvgString(obj: SmileyObject): string {
  const def = SMILEY_ICONS[obj.icon as keyof typeof SMILEY_ICONS] ?? SMILEY_ICONS[DEFAULT_SMILEY_ICON];
  return tintFill(def.svg, obj.fill);
}

/** Data-URL per (motif + fill), built once and reused. */
const urlCache = new Map<string, string>();
function iconDataUrl(obj: SmileyObject): string {
  const sig = iconSignature(obj);
  let url = urlCache.get(sig);
  if (url === undefined) {
    url = `data:image/svg+xml,${encodeURIComponent(smileySvgString(obj))}`;
    urlCache.set(sig, url);
  }
  return url;
}

/**
 * Load the (possibly tinted) motif SVG into the image node. The render signature
 * is stored as `iconSig` so `updateNode` can skip a reload when nothing changed.
 * Guarded for non-DOM environments (unit tests) where `Image` is absent.
 */
function loadImage(node: Konva.Image, obj: SmileyObject): void {
  node.setAttr('iconSig', iconSignature(obj));
  if (typeof Image === 'undefined') return;
  const el = new Image();
  el.onload = (): void => {
    node.image(el);
    node.getLayer()?.batchDraw();
  };
  el.src = iconDataUrl(obj);
  if (el.complete && el.naturalWidth > 0) node.image(el);
}

/** Build a fresh Konva.Group rendering the smiley. */
export function createNode(obj: SmileyObject, _ctx: SliceRenderContext): Konva.Group {
  const group = new Konva.Group({
    id: obj.id, x: obj.x, y: obj.y,
    width: obj.width, height: obj.height,
    rotation: obj.rotation, name: 'smiley-obj',
  });

  // Transparent hit area covering the full bounding box — the image has
  // listening:false, so without this rect the group could not be selected.
  const hit = new Konva.Rect({
    name: SMILEY_HIT_NAME,
    x: 0, y: 0, width: obj.width, height: obj.height,
    fill: 'rgba(0,0,0,0)',
  });

  const box = iconBox(obj);
  const image = new Konva.Image({
    name: SMILEY_IMAGE_NAME,
    x: box.x, y: box.y, width: box.width, height: box.height,
    image: undefined,
    listening: false,
  });
  loadImage(image, obj);

  group.add(hit);
  group.add(image);
  return group;
}

/** Apply changed properties from an updated SmileyObject onto its node. */
export function updateNode(node: Konva.Node, obj: SmileyObject, _ctx: SliceRenderContext): void {
  if (!(node instanceof Konva.Group)) return;

  const hit = node.findOne<Konva.Rect>(`.${SMILEY_HIT_NAME}`);
  if (hit) hit.setAttrs({ width: obj.width, height: obj.height });

  const image = node.findOne<Konva.Image>(`.${SMILEY_IMAGE_NAME}`);
  if (!image) return;
  const box = iconBox(obj);
  image.setAttrs({ x: box.x, y: box.y, width: box.width, height: box.height });
  // Reload the bitmap only when the motif OR the fill actually changed.
  if (image.getAttr('iconSig') !== iconSignature(obj)) loadImage(image, obj);
}
